import type { PointerEvent as ReactPointerEvent } from 'react'

import { useEffect, useRef } from 'react'

/**
 * Pulls an element toward the cursor while it is hovered. Pair with
 * `transition-transform duration-300` so the element trails rather than snaps.
 *
 * Mouse only, and off entirely under `prefers-reduced-motion`.
 */
export const useMagnetic = <T extends HTMLElement>(strength = 0.22) => {
    const ref = useRef<T | null>(null)
    const enabled = useRef(false)

    useEffect(() => {
        enabled.current =
            !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
            window.matchMedia('(hover: hover)').matches
    }, [])

    const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
        const el = ref.current
        if (!el || !enabled.current || event.pointerType !== 'mouse') return

        const rect = el.getBoundingClientRect()
        const dx = event.clientX - (rect.left + rect.width / 2)
        const dy = event.clientY - (rect.top + rect.height / 2)

        el.style.transform = `translate3d(${(dx * strength).toFixed(2)}px, ${(dy * strength).toFixed(2)}px, 0)`
    }

    const onPointerLeave = () => {
        const el = ref.current
        if (!el) return

        el.style.transform = 'translate3d(0, 0, 0)'
    }

    return { ref, onPointerMove, onPointerLeave }
}
